export type PayrollOverviewColumnKey =
	| 'payGroups'
	| 'currency'
	| 'paySlips'
	| 'createdBy'
	| 'totalPayable'
	| 'status'
	| 'bankFile'
	| 'comments'
	| 'initiatePayment'
	| 'checkStatus'
	| 'moveToHistory';

export type PayrollOverviewFixedColumnKey = 'payPeriod' | 'action';

export interface ColumnPreference {
	key: PayrollOverviewColumnKey;
	visible: boolean;
}

export const CUSTOMIZABLE_COLUMN_KEYS: PayrollOverviewColumnKey[] = [
	'payGroups',
	'currency',
	'paySlips',
	'createdBy',
	'totalPayable',
	'status',
	'bankFile',
	'comments',
	'initiatePayment',
	'checkStatus',
	'moveToHistory',
];

export const COLUMN_LABELS: Record<
	PayrollOverviewColumnKey | PayrollOverviewFixedColumnKey,
	string
> = {
	payPeriod: 'Pay Period',
	payGroups: 'Pay Group',
	currency: 'Currency',
	paySlips: 'Combine Pay Slip',
	createdBy: 'Submitted By',
	totalPayable: 'Total Payable',
	status: 'Status',
	bankFile: 'Bank File',
	comments: 'Comments',
	action: 'Action',
	initiatePayment: 'Initiate Payment',
	checkStatus: 'Check Status',
	moveToHistory: 'Archive',
};

export const REQUIRED_COLUMN_COUNT = CUSTOMIZABLE_COLUMN_KEYS.length;

export const DEFAULT_COLUMN_PREFERENCES: ColumnPreference[] =
	CUSTOMIZABLE_COLUMN_KEYS.map((key) => ({
		key,
		visible: true,
	}));

/** Merges partial/legacy GET responses with defaults (backend resolveUiPreferences). */
export const normalizeColumnPreferences = (
	apiColumns?: ColumnPreference[]
): ColumnPreference[] => {
	if (!apiColumns?.length) {
		return [...DEFAULT_COLUMN_PREFERENCES];
	}

	const validKeys = new Set(CUSTOMIZABLE_COLUMN_KEYS);
	const seen = new Set<PayrollOverviewColumnKey>();
	const normalized: ColumnPreference[] = [];

	for (const column of apiColumns) {
		if (!validKeys.has(column.key) || seen.has(column.key)) {
			continue;
		}
		seen.add(column.key);
		normalized.push({
			key: column.key,
			visible: Boolean(column.visible),
		});
	}

	for (const key of CUSTOMIZABLE_COLUMN_KEYS) {
		if (!seen.has(key)) {
			normalized.push({ key, visible: true });
		}
	}

	return normalized;
};

/** Ensures PATCH payload has exactly 11 allowed keys (no payPeriod/action). */
export const preparePatchColumnPreferences = (
	columns: ColumnPreference[]
): ColumnPreference[] => {
	const normalized = normalizeColumnPreferences(columns);

	if (normalized.length !== REQUIRED_COLUMN_COUNT) {
		throw new Error(
			'payrollOverview.columns must contain exactly 11 columns'
		);
	}

	const keys = normalized.map((column) => column.key);
	const uniqueKeys = new Set(keys);

	if (uniqueKeys.size !== REQUIRED_COLUMN_COUNT) {
		const duplicateKey = keys.find(
			(key, index) => keys.indexOf(key) !== index
		);
		throw new Error(`Duplicate column key: ${duplicateKey}`);
	}

	for (const key of CUSTOMIZABLE_COLUMN_KEYS) {
		if (!uniqueKeys.has(key)) {
			throw new Error(`Missing column key: ${key}`);
		}
	}

	return normalized.map((column) => ({
		key: column.key,
		visible: Boolean(column.visible),
	}));
};